<template>
  <div>
    <b-card>
      <b-row class="mt-1">
        <b-col xl="4" lg="6" md="8" sm="12" cols="12">
          <b-form-group label="Date" label-for="date">
            <flat-pickr
              v-model="filters.date"
              :config="dateConfig"
              class="form-control"
              :placeholder="$t('Select Date')"
              @on-change="onDateChange"
            />
          </b-form-group>
        </b-col>

        <b-col xl="8" lg="6" md="4" sm="12" cols="12">
          <div class="d-flex gap-2">
            <b-button
              v-if="hasActiveFilters"
              variant="outline-secondary"
              @click="clearFilters"
              class="mt-2 mr-2"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
            <!-- <b-button v-if="rights.view" variant="outline-primary" @click="openPrintModal" class="mt-2">
              <feather-icon icon="PrinterIcon" size="16" />
            </b-button> -->
          </div>
        </b-col>
      </b-row>

      <!-- Data Grid -->
      <b-table
        class="mt-1"
        :items="lateApprovals"
        :fields="fields"
        :busy="dataLoading"
        responsive
        hover
        show-empty
        empty-text="No late approvals found"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner variant="primary" />
          </div>
        </template>

        <template #cell(name)="{ item }">
          <div class="staff-name-cell">
            <div class="staff-name">
              {{ (item.stf && item.stf.name) || "-" }}
            </div>
            <small class="text-muted">{{
              (item.stf && item.stf.designation) || ""
            }}</small>
          </div>
        </template>

        <template #cell(time)="{ item }">
          <div class="time-cell">
            <div class="time-row">
              <span class="time-label">Check In:</span>
              <b-badge variant="light-warning">{{
                formatTime(item.us && item.us.timeIn)
              }}</b-badge>
            </div>
            <div class="time-row" v-if="item.us && item.us.lateMinutes">
              <span class="time-label">Late:</span>
              <b-badge variant="light-danger"
                >{{ item.us.lateMinutes }} min</b-badge
              >
            </div>
          </div>
        </template>

        <template #cell(status)="{ item }">
          <b-badge
            :variant="getStatusVariant(item.us && item.us.status)"
            class="status-badge"
          >
            {{ (item.us && item.us.status) || "Pending" }}
          </b-badge>
          <!-- Debug info -->
        </template>

        <template #cell(reason)="{ item }">
          <div v-if="item.us && item.us.reason" class="reason-cell">
            <div
              class="reason-text"
              @click="openNoteModal(item.us.reason, 'Late Reason', item)"
            >
              <feather-icon icon="MessageSquareIcon" size="12" class="mr-1" />
              {{ truncateText(item.us.reason, 30) }}
            </div>
          </div>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(approvalNote)="{ item }">
          <div
            v-if="item.us && item.us.approval_note"
            class="approval-note-cell"
          >
            <div
              class="approval-note-text"
              @click="
                openNoteModal(item.us.approval_note, 'Approval Note', item)
              "
            >
              <feather-icon icon="UserCheckIcon" size="12" class="mr-1" />
              {{ truncateText(item.us.approval_note, 30) }}
            </div>
          </div>
          <span v-else class="text-muted">-</span>
          <!-- Debug info -->
        </template>

        <template #cell(sno)="{ index }">
          <span class="serial-number">{{
            (currentPage - 1) * pageSize + index + 1
          }}</span>
        </template>

        <template #cell(date)="{ item }">
          <div class="date-cell">
            <div class="date-badge">
              {{ formatDate(item.us && item.us.date) }}
            </div>
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <div class="d-flex align-items-center">
            <b-button
              v-if="rights.edit && item.us && item.us.status === 'pending'"
              variant="outline-success"
              v-b-tooltip.hover.top
              title="Approve"
              class="btn-icon mr-1"
              @click="openApproveModal(item)"
            >
              <feather-icon icon="CheckIcon" />
            </b-button>

            <b-button
              v-if="rights.edit && item.us && item.us.status === 'pending'"
              variant="outline-danger"
              v-b-tooltip.hover.right
              title="Reject"
              class="btn-icon"
              @click="openRejectModal(item)"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-row class="mt-3">
        <b-col>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRecords"
            :per-page="pageSize"
            align="center"
            @change="onPageChange"
          />
        </b-col>
      </b-row>
    </b-card>

    <!-- View Late Approval Modal -->
    <b-modal
      v-model="showModal"
      title="Late Approval Details"
      @hidden="selectedLateApproval = null"
      centered
      no-close-on-backdrop
      size="xl"
    >
      <div v-if="selectedLateApproval">
        <!-- Employee Information -->
        <b-card class="mb-1">
          <b-row>
            <b-col md="6" class="border-right">
              <div class="d-flex align-items-center mb-1">
                <div class="employee-avatar-small">
                  <feather-icon icon="UserIcon" size="12" />
                </div>
                <h6 class="mb-0 ml-1 compact-title">Employee Information</h6>
              </div>
              <b-row>
                <b-col md="6" class="mb-1">
                  <div class="info-item-ultra-compact">
                    <label class="info-label-ultra-compact">ID</label>
                    <div class="info-value-ultra-compact primary">
                      {{
                        selectedLateApproval.us && selectedLateApproval.us.tID
                      }}
                    </div>
                  </div>
                </b-col>
                <b-col md="6" class="mb-1">
                  <div class="info-item-ultra-compact">
                    <label class="info-label-ultra-compact">Name</label>
                    <div class="info-value-ultra-compact primary">
                      {{
                        selectedLateApproval.stf &&
                        selectedLateApproval.stf.name
                      }}
                    </div>
                  </div>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6" class="mb-1">
                  <div class="info-item-ultra-compact">
                    <label class="info-label-ultra-compact">Designation</label>
                    <div class="info-value-ultra-compact info">
                      {{
                        selectedLateApproval.stf &&
                        selectedLateApproval.stf.designation
                      }}
                    </div>
                  </div>
                </b-col>
                <b-col md="6" class="mb-1">
                  <div class="info-item-ultra-compact">
                    <label class="info-label-ultra-compact">Department</label>
                    <div class="info-value-ultra-compact secondary">
                      {{
                        selectedLateApproval.stf &&
                        selectedLateApproval.stf.departmentID
                      }}
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-col>

            <!-- Late Approval Information -->
            <b-col md="6">
              <div class="d-flex align-items-center mb-1">
                <div class="employee-avatar-small">
                  <feather-icon icon="ClockIcon" size="12" />
                </div>
                <h6 class="mb-0 ml-1 compact-title">
                  Late Approval Information
                </h6>
              </div>
              <b-row>
                <b-col md="6" class="mb-1">
                  <div class="info-item-ultra-compact">
                    <label class="info-label-ultra-compact"
                      >Check In Time</label
                    >
                    <div class="info-value-ultra-compact warning">
                      {{
                        formatTime(
                          selectedLateApproval.us &&
                            selectedLateApproval.us.timeIn
                        )
                      }}
                    </div>
                  </div>
                </b-col>
                <b-col md="6" class="mb-1">
                  <div class="info-item-ultra-compact">
                    <label class="info-label-ultra-compact">Date</label>
                    <div class="info-value-ultra-compact info">
                      {{
                        formatDate(
                          selectedLateApproval.us &&
                            selectedLateApproval.us.date
                        )
                      }}
                    </div>
                  </div>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6" class="mb-1">
                  <div class="info-item-ultra-compact">
                    <label class="info-label-ultra-compact">Late Minutes</label>
                    <div class="info-value-ultra-compact danger">
                      {{
                        (selectedLateApproval.us &&
                          selectedLateApproval.us.lateMinutes) ||
                        "N/A"
                      }}
                      min
                    </div>
                  </div>
                </b-col>
                <b-col md="6" class="mb-1">
                  <div class="info-item-ultra-compact">
                    <label class="info-label-ultra-compact">Status</label>
                    <div class="info-value-ultra-compact">
                      <b-badge
                        :variant="
                          getStatusVariant(
                            selectedLateApproval.us &&
                              selectedLateApproval.us.status
                          )
                        "
                      >
                        {{
                          selectedLateApproval.us &&
                          selectedLateApproval.us.status
                        }}
                      </b-badge>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-card>

        <!-- Reason and Notes -->
        <b-card class="mb-1">
          <b-row>
            <b-col md="6" class="border-right">
              <div class="d-flex align-items-center mb-1">
                <div class="employee-avatar-small">
                  <feather-icon icon="MessageSquareIcon" size="12" />
                </div>
                <h6 class="mb-0 ml-1 compact-title">Late Reason</h6>
              </div>
              <div class="reason-content">
                {{
                  (selectedLateApproval.us && selectedLateApproval.us.reason) ||
                  "No reason provided"
                }}
              </div>
            </b-col>

            <b-col md="6">
              <div class="d-flex align-items-center mb-1">
                <div class="employee-avatar-small">
                  <feather-icon icon="UserCheckIcon" size="12" />
                </div>
                <h6 class="mb-0 ml-1 compact-title">Approval Note</h6>
              </div>
              <div class="reason-content">
                {{
                  (selectedLateApproval.us &&
                    selectedLateApproval.us.approval_note) ||
                  "No approval note"
                }}
              </div>
            </b-col>
          </b-row>
        </b-card>
      </div>
    </b-modal>

    <!-- Approve Modal -->
    <b-modal
      v-model="showApproveModal"
      title="Approve Late Arrival"
      @hidden="resetApproveForm"
      centered
      no-close-on-backdrop
    >
      <b-form @submit.prevent="approveLateApproval">
        <b-form-group label="Approval Note" label-for="approvalNote">
          <b-form-textarea
            id="approvalNote"
            v-model="approveForm.approvalNote"
            placeholder="Enter approval note (optional)"
            rows="3"
            :state="approveForm.approvalNote ? null : false"
          />
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="showApproveModal = false">
          Cancel
        </b-button>
        <b-button
          variant="success"
          @click="approveLateApproval"
          :disabled="!isApproveFormValid || approveLoading"
        >
          <b-spinner v-if="approveLoading" small class="mr-1" />
          {{ approveLoading ? "Approving..." : "Approve" }}
        </b-button>
      </template>
    </b-modal>

    <!-- Reject Modal -->
    <b-modal
      v-model="showRejectModal"
      title="Reject Late Arrival"
      @hidden="resetRejectForm"
      centered
      no-close-on-backdrop
    >
      <b-form @submit.prevent="rejectLateApproval">
        <b-form-group label="Rejection Note" label-for="rejectionNote">
          <b-form-textarea
            id="rejectionNote"
            v-model="rejectForm.rejectionNote"
            placeholder="Enter rejection note"
            rows="3"
            :state="rejectForm.rejectionNote ? null : false"
            required
          />
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="showRejectModal = false">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          @click="rejectLateApproval"
          :disabled="!isRejectFormValid || rejectLoading"
        >
          <b-spinner v-if="rejectLoading" small class="mr-1" />
          {{ rejectLoading ? "Rejecting..." : "Reject" }}
        </b-button>
      </template>
    </b-modal>

    <!-- Note Modal -->
    <b-modal
      v-model="showNoteModal"
      :title="noteModalTitle"
      @hidden="resetNoteModal"
      centered
      size="lg"
    >
      <div class="note-content">
        {{ selectedNote }}
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

// Import Bootstrap Vue components
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BTable,
  BSpinner,
  BBadge,
  BModal,
  BForm,
  BFormTextarea,
  BPagination,
  BTooltip,
} from "bootstrap-vue";

export default {
  name: "LateApproval",
  components: {
    flatPickr,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTable,
    BSpinner,
    BBadge,
    BModal,
    BForm,
    BFormTextarea,
    BPagination,
    BTooltip,
  },
  data() {
    return {
      rights: { view: true, edit: true, add: true },
      lateApprovals: [],
      showModal: false,
      selectedLateApproval: null,
      dataLoading: false,
      viewLoading: null,
      showApproveModal: false,
      showRejectModal: false,
      showNoteModal: false,
      selectedNote: "",
      noteModalTitle: "",

      // Filters
      filters: {
        date: new Date().toISOString().split("T")[0], // Today's date by default
      },

      // Options
      campusOptions: [],
      departmentOptions: [],
      staffOptions: [],
      statusOptions: [
        { value: "Pending", text: "Pending" },
        { value: "Approved", text: "Approved" },
        { value: "Rejected", text: "Rejected" },
      ],

      // Pagination
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,

      // Forms
      approveForm: {
        approvalNote: "",
      },
      rejectForm: {
        rejectionNote: "",
      },

      // Loading states
      campusesLoading: false,
      staffLoading: false,
      approveLoading: false,
      rejectLoading: false,

      // Date picker config
      dateConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
      },

      // Table fields
      fields: [
        { key: "sno", label: "S.No", sortable: false },
        { key: "name", label: "Staff Name", sortable: false },
        { key: "date", label: "Date", sortable: false },
        { key: "time", label: "Time", sortable: false },
        { key: "status", label: "Status", sortable: false },
        { key: "reason", label: "Reason", sortable: false },
        { key: "approvalNote", label: "Approval Note", sortable: false },
        { key: "actions", label: "Actions", sortable: false },
      ],
    };
  },
  computed: {
    hasActiveFilters() {
      return this.filters.date !== new Date().toISOString().split("T")[0];
    },

    isApproveFormValid() {
      return true; // Approval note is optional
    },

    isRejectFormValid() {
      return (
        this.rejectForm.rejectionNote &&
        this.rejectForm.rejectionNote.trim() !== ""
      );
    },

    shouldShowCampusDropdown() {
      // Show campus dropdown if user has access to multiple campuses
      return this.campusOptions.length > 1;
    },
  },
  created() {
    this.loadSettings();
    this.loadLateApprovals();
  },
  mounted() {
    // Force load data on mount for debugging
    console.log("Component mounted, loading data...");
    this.loadLateApprovals();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    async loadSettings() {
      try {
        // Load campuses
        const campusObj = {
          url: `${this.$store.state.domain}campuses?db=${this.$store.state.userData.db}&cId=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token,
        };
        const campusResponse = await this.get(campusObj);
        this.campusOptions = campusResponse.data || [];

        // Load departments for current campus
        if (this.filters.campusID) {
          await this.loadDepartments();
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    },

    async loadDepartments() {
      try {
        const obj = {
          url: `${this.$store.state.domain}departments?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&campusID=${this.filters.campusID}`,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.departmentOptions = response.data || [];
      } catch (error) {
        console.error("Error loading departments:", error);
        this.departmentOptions = [];
      }
    },

    async loadStaff() {
      if (!this.filters.departmentID) {
        this.staffOptions = [];
        return;
      }

      try {
        this.staffLoading = true;
        const obj = {
          url: `${this.$store.state.domain}staff?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&campusID=${this.filters.campusID}&departmentID=${this.filters.departmentID}`,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.staffOptions = response.data || [];
      } catch (error) {
        console.error("Error loading staff:", error);
        this.staffOptions = [];
      } finally {
        this.staffLoading = false;
      }
    },

    async loadLateApprovals() {
      try {
        this.dataLoading = true;

        // Build URL with filters
        let url = `${this.$store.state.domain}LateApprovals/LoadData?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`;

        // Add date filter (optional)
        if (this.filters.date) {
          url += `&date=${this.filters.date}`;
        }

        console.log("API URL:", url);
        console.log("User Data:", this.$store.state.userData);

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        console.log("Full API Response:", response);
        console.log("Response Data:", response.data);

        // Handle the response data structure
        if (response && response.data && Array.isArray(response.data)) {
          this.lateApprovals = response.data;
          this.totalRecords = response.data.length;
          console.log("Late Approvals Data:", this.lateApprovals);
          console.log("Total Records:", this.totalRecords);
        } else if (response && Array.isArray(response)) {
          this.lateApprovals = response;
          this.totalRecords = response.length;
          console.log("Late Approvals Data:", this.lateApprovals);
          console.log("Total Records:", this.totalRecords);
        } else {
          // No data found for the selected date
          this.lateApprovals = [];
          this.totalRecords = 0;
          console.log("No data found for the selected date");
        }
      } catch (error) {
        console.error("Error loading late approvals:", error);
        this.lateApprovals = [];
        this.totalRecords = 0;
        this.$bvToast.toast("Error loading late approvals: " + error.message, {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
      }
    },

    async approveLateApproval() {
      try {
        this.approveLoading = true;

        const requestData = {
          id: this.selectedLateApproval.us && this.selectedLateApproval.us.id,
          userID: this.$store.state.userData.userID,
          status: "approved",
          approval_note: this.approveForm.approvalNote || "",
        };

        console.log("Approving late approval with data:", requestData);

        const obj = {
          url: `${this.$store.state.domain}LateApprovals/ChangeStatus?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token,
          body: requestData,
        };

        // Use axios directly to avoid store toast issues
        const axios = require("axios");
        const response = await axios.post(obj.url, obj.body, {
          headers: {
            Authorization: `Bearer ${obj.token}`,
            "Content-Type": "application/json",
          },
        });
        const responseData = response.data;

        console.log("Approval response:", responseData);

        // Check if the response indicates success - handle different response structures
        const isSuccess =
          responseData &&
          (responseData.status === "success" ||
            responseData.message ||
            (responseData.data && responseData.data.status === "Approved") ||
            (responseData.data &&
              responseData.data.data &&
              responseData.data.data.status === "Approved") ||
            (responseData.data && responseData.data.status) || // Any status in data indicates success
            responseData.data);

        console.log("Is Success:", isSuccess);

        if (isSuccess) {
          this.$bvToast.toast(
            responseData.message ||
              "Late arrival has been approved successfully.",
            {
              title: "Approved!",
              variant: "success",
              solid: true,
            }
          );
          this.showApproveModal = false;
          this.resetApproveForm();
          this.loadLateApprovals();
        } else {
          console.log("Approve failed - Response:", responseData);
          this.$bvToast.toast("Failed to approve late arrival.", {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          });
        }
      } catch (error) {
        console.error("Error approving late arrival:", error);
        this.$bvToast.toast("An error occurred while approving the request.", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        // Close modal even on error after showing the error message
        setTimeout(() => {
          this.showApproveModal = false;
        }, 2000);
      } finally {
        this.approveLoading = false;
      }
    },

    async rejectLateApproval() {
      try {
        this.rejectLoading = true;

        const requestData = {
          id: this.selectedLateApproval.us && this.selectedLateApproval.us.id,
          userID: this.$store.state.userData.userID,
          status: "rejected",
          approval_note: this.rejectForm.rejectionNote,
        };

        console.log("Rejecting late approval with data:", requestData);

        const obj = {
          url: `${this.$store.state.domain}LateApprovals/ChangeStatus?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token,
          body: requestData,
        };

        // Use axios directly to avoid store toast issues
        const axios = require("axios");
        const response = await axios.post(obj.url, obj.body, {
          headers: {
            Authorization: `Bearer ${obj.token}`,
            "Content-Type": "application/json",
          },
        });
        const responseData = response.data;

        console.log("Rejection response:", responseData);

        // Check if the response indicates success - handle different response structures
        const isSuccess =
          responseData &&
          (responseData.status === "success" ||
            responseData.message ||
            (responseData.data && responseData.data.status === "Rejected") ||
            (responseData.data &&
              responseData.data.data &&
              responseData.data.data.status === "Rejected") ||
            (responseData.data && responseData.data.status) || // Any status in data indicates success
            responseData.data);

        console.log("Is Success:", isSuccess);

        if (isSuccess) {
          this.$bvToast.toast(
            responseData.message ||
              "Late arrival has been rejected successfully.",
            {
              title: "Rejected!",
              variant: "success",
              solid: true,
            }
          );
          this.showRejectModal = false;
          this.resetRejectForm();
          this.loadLateApprovals();
        } else {
          console.log("Reject failed - Response:", responseData);
          this.$bvToast.toast("Failed to reject late arrival.", {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          });
        }
      } catch (error) {
        console.error("Error rejecting late arrival:", error);
        this.$bvToast.toast("An error occurred while rejecting the request.", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        // Close modal even on error after showing the error message
        setTimeout(() => {
          this.showRejectModal = false;
        }, 2000);
      } finally {
        this.rejectLoading = false;
      }
    },

    // Event handlers
    onCampusChange() {
      this.filters.departmentID = null;
      this.filters.staffID = null;
      this.departmentOptions = [];
      this.staffOptions = [];
      this.loadDepartments();
      this.loadLateApprovals();
    },

    onDepartmentChange() {
      this.filters.staffID = null;
      this.staffOptions = [];
      this.loadStaff();
      this.loadLateApprovals();
    },

    onFilterChange() {
      this.loadLateApprovals();
    },

    onDateChange() {
      this.loadLateApprovals();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadLateApprovals();
    },

    // Modal handlers
    viewLateApproval(item) {
      this.selectedLateApproval = item;
      this.showModal = true;
    },

    openApproveModal(item) {
      this.selectedLateApproval = item;
      this.showApproveModal = true;
    },

    openRejectModal(item) {
      this.selectedLateApproval = item;
      this.showRejectModal = true;
    },

    openNoteModal(note, title, item) {
      this.selectedNote = note;
      this.noteModalTitle = title;
      this.showNoteModal = true;
    },

    // Form resets
    resetApproveForm() {
      this.approveForm = {
        approvalNote: "",
      };
      this.selectedLateApproval = null;
    },

    resetRejectForm() {
      this.rejectForm = {
        rejectionNote: "",
      };
      this.selectedLateApproval = null;
    },

    resetNoteModal() {
      this.selectedNote = "";
      this.noteModalTitle = "";
    },

    // Utility methods
    clearFilters() {
      this.filters = {
        date: new Date().toISOString().split("T")[0],
      };
      this.loadLateApprovals();
    },

    getStatusVariant(status) {
      if (!status) return "secondary";
      switch (status.toLowerCase()) {
        case "approved":
          return "success";
        case "rejected":
          return "danger";
        case "pending":
          return "warning";
        default:
          return "secondary";
      }
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString();
    },

    formatTime(time) {
      if (!time) return "-";
      // Handle both time strings and full datetime strings
      let timeString = time;
      if (time.includes("T")) {
        // If it's a full datetime string, extract just the time part
        timeString = time.split("T")[1];
      }
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "-";
      return new Date(dateTime).toLocaleString();
    },

    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },

    openPrintModal() {
      // Implement print functionality
      this.$bvToast.toast("Print functionality not implemented yet", {
        title: "Info",
        variant: "info",
        solid: true,
      });
    },
  },
};
</script>

<style scoped>
.combined-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.employee-avatar-small {
  width: 24px;
  height: 24px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

.compact-title {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

.info-item-ultra-compact {
  margin-bottom: 4px;
}

.info-label-ultra-compact {
  font-size: 11px;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 2px;
  display: block;
}

.info-value-ultra-compact {
  font-size: 12px;
  font-weight: 600;
  color: #495057;
}

.info-value-ultra-compact.primary {
  color: #007bff;
}

.info-value-ultra-compact.info {
  color: #17a2b8;
}

.info-value-ultra-compact.secondary {
  color: #6c757d;
}

.info-value-ultra-compact.success {
  color: #28a745;
}

.info-value-ultra-compact.warning {
  color: #ffc107;
}

.info-value-ultra-compact.danger {
  color: #dc3545;
}

.reason-content {
  font-size: 13px;
  color: #495057;
  background: #f8f9fa;
  padding: 8px;
  border-radius: 4px;
  border-left: 3px solid #007bff;
}

.note-cell {
  max-width: 200px;
}

.note-badge {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.note-badge:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.note-badge.late-reason {
  border-left: 3px solid #ffc107;
}

.note-badge.admin-note {
  border-left: 3px solid #007bff;
}

.btn-icon {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Table Cell Styles */
.staff-name-cell {
  min-width: 120px;
}

.staff-name {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
}

.time-cell {
  min-width: 140px;
}

.time-row {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  font-size: 12px;
}

.time-label {
  font-weight: 500;
  color: #6c757d;
  margin-right: 6px;
  min-width: 50px;
}

.reason-cell,
.approval-note-cell {
  max-width: 200px;
}

.reason-text,
.approval-note-text {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.reason-text:hover,
.approval-note-text:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.reason-text {
  border-left: 3px solid #ffc107;
}

.approval-note-text {
  border-left: 3px solid #007bff;
}

.serial-number {
  font-weight: 600;
  color: #6c757d;
  font-size: 13px;
}

.date-cell {
  min-width: 100px;
}

.date-badge {
  font-weight: 600;
  color: #495057;
  font-size: 13px;
  margin-bottom: 2px;
}

/* Status badge styling */
.status-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 20px;
  text-transform: capitalize;
}
</style>
